import { MIN_MEANINGFUL_TEAM_GAMES } from "./statMeaningfulness.ts";

/**
 * C4 data-story true semantics (MOBILE_CONTENT_FIRST_PLAN_2026-07-06.md).
 *
 * A "story" is a meaningful conclusion computed from real view data — a league
 * rank, a net margin, a team scoring leader — never a filter-state description
 * or an unsupported platitude. Every generator here is a pure function covered
 * by statInsights.test.ts; every conclusion carries the evidence rows that
 * back it. Rendering goes through MobileStatInsights.tsx (one conclusion line,
 * tap to expand evidence, at most 2 per page); when no conclusion meets its
 * gate the whole block does not render (A3 statMeaningfulness precedent).
 *
 * i18n stays out of this module by design: generators return structured data
 * and the landing views translate it, so the computations remain pure and the
 * tests assert numbers, not phrasing.
 */

/** Max conclusions per page (C4 plan: 每页最多 2 条). */
export const MAX_STAT_INSIGHTS_PER_PAGE = 2;

/** Default rank threshold: only a top-5 league rank is a conclusion. */
export const DEFAULT_INSIGHT_MAX_RANK = 5;

/** Default league-size floor: tiny distributions make trivial "ranks". */
export const DEFAULT_INSIGHT_MIN_ENTRIES = 8;

/** Evidence rows shown when a conclusion is expanded. */
export const DEFAULT_INSIGHT_EVIDENCE_COUNT = 3;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  // playersPlus stat records can be [value, gid] pairs (gameHighs style).
  if (
    Array.isArray(value) &&
    typeof value[0] === "number" &&
    Number.isFinite(value[0])
  ) {
    return value[0];
  }

  return undefined;
};

/** Shared numeric extraction for view stat records (number or [number, ...]). */
export const getInsightStatValue = toFiniteNumber;

export type LeagueRankPlacement = {
  /** Every ranked value is identical — no differentiation, no conclusion. */
  allEqual: boolean;
  /** 1-based; tied values share the best rank (two teams at +8.0 are both #1). */
  rank: number;
  tied: boolean;
  total: number;
};

/**
 * Rank a value inside a league distribution. Pure math, no gates: ties share
 * the best rank (rank = 1 + count of strictly better values).
 */
export const getLeagueRankPlacement = ({
  lowerIsBetter = false,
  value,
  values,
}: {
  lowerIsBetter?: boolean;
  value: number;
  values: readonly number[];
}): LeagueRankPlacement | undefined => {
  if (!Number.isFinite(value)) {
    return undefined;
  }

  const finiteValues = values.filter((other) => Number.isFinite(other));
  if (finiteValues.length === 0) {
    return undefined;
  }

  let better = 0;
  let equal = 0;
  for (const other of finiteValues) {
    if (other === value) {
      equal += 1;
    } else if (lowerIsBetter ? other < value : other > value) {
      better += 1;
    }
  }

  return {
    allEqual: equal === finiteValues.length,
    rank: better + 1,
    tied: equal > 1,
    total: finiteValues.length,
  };
};

export type StatInsightEvidenceRow<Subject> = {
  isSubject: boolean;
  /** Shared rank (ties share the best rank), consistent with the conclusion. */
  rank: number;
  subject: Subject;
  value: number;
};

/**
 * Evidence rows for a rank conclusion: the league top-N for the metric plus
 * the subject's own row (appended when it is not already inside the top-N).
 */
export const getLeagueRankEvidence = <Subject>({
  entries,
  getValue,
  isSubject,
  lowerIsBetter = false,
  topCount = DEFAULT_INSIGHT_EVIDENCE_COUNT,
}: {
  entries: readonly Subject[];
  getValue: (subject: Subject) => number | undefined;
  isSubject: (subject: Subject) => boolean;
  lowerIsBetter?: boolean;
  topCount?: number;
}): StatInsightEvidenceRow<Subject>[] => {
  const valued = entries
    .map((subject) => ({ subject, value: getValue(subject) }))
    .filter((entry): entry is { subject: Subject; value: number } =>
      entry.value !== undefined && Number.isFinite(entry.value),
    );

  if (valued.length === 0) {
    return [];
  }

  const values = valued.map((entry) => entry.value);
  const rankOf = (value: number) =>
    getLeagueRankPlacement({ lowerIsBetter, value, values })?.rank ?? 0;

  // Stable sort: best first, original order preserved inside ties.
  const sorted = [...valued].sort((a, b) =>
    lowerIsBetter ? a.value - b.value : b.value - a.value,
  );

  const rows: StatInsightEvidenceRow<Subject>[] = sorted
    .slice(0, Math.max(0, topCount))
    .map((entry) => ({
      isSubject: isSubject(entry.subject),
      rank: rankOf(entry.value),
      subject: entry.subject,
      value: entry.value,
    }));

  if (!rows.some((row) => row.isSubject)) {
    const subjectEntry = valued.find((entry) => isSubject(entry.subject));
    if (subjectEntry) {
      rows.push({
        isSubject: true,
        rank: rankOf(subjectEntry.value),
        subject: subjectEntry.subject,
        value: subjectEntry.value,
      });
    }
  }

  return rows;
};

export type LeagueRankConclusion<Subject> = {
  evidence: StatInsightEvidenceRow<Subject>[];
  placement: LeagueRankPlacement;
  subject: Subject;
  value: number;
};

export type LeagueRankConclusionGates = {
  /** undefined (scope has no current-season games) always means: no conclusion. */
  gamesPlayed: number | undefined;
  lowerIsBetter?: boolean;
  maxRank?: number;
  minEntries?: number;
  minGames?: number;
  /**
   * Lowest rank allowed to conclude. PlayerStats passes 2: the league #1 is
   * already the first-screen leader row, so concluding it again would repeat
   * the same information unit (法则二).
   */
  minRank?: number;
  topCount?: number;
};

/**
 * The gated core: a league-rank conclusion exists only when the subject has
 * enough games (A3 threshold), the distribution is big enough and actually
 * differentiated, and the subject's rank clears the thresholds.
 */
export const getLeagueRankConclusion = <Subject>({
  entries,
  gates,
  getValue,
  isSubject,
}: {
  entries: readonly Subject[];
  gates: LeagueRankConclusionGates;
  getValue: (subject: Subject) => number | undefined;
  isSubject: (subject: Subject) => boolean;
}): LeagueRankConclusion<Subject> | undefined => {
  const {
    gamesPlayed,
    lowerIsBetter = false,
    maxRank = DEFAULT_INSIGHT_MAX_RANK,
    minEntries = DEFAULT_INSIGHT_MIN_ENTRIES,
    minGames = MIN_MEANINGFUL_TEAM_GAMES,
    minRank = 1,
    topCount = DEFAULT_INSIGHT_EVIDENCE_COUNT,
  } = gates;

  if (gamesPlayed === undefined || gamesPlayed < minGames) {
    return undefined;
  }

  const valued = entries
    .map((subject) => ({ subject, value: getValue(subject) }))
    .filter((entry): entry is { subject: Subject; value: number } =>
      entry.value !== undefined && Number.isFinite(entry.value),
    );

  if (valued.length < minEntries) {
    return undefined;
  }

  const subjectEntry = valued.find((entry) => isSubject(entry.subject));
  if (!subjectEntry) {
    return undefined;
  }

  const placement = getLeagueRankPlacement({
    lowerIsBetter,
    value: subjectEntry.value,
    values: valued.map((entry) => entry.value),
  });

  if (
    !placement ||
    placement.allEqual ||
    placement.rank > maxRank ||
    placement.rank < minRank
  ) {
    return undefined;
  }

  return {
    evidence: getLeagueRankEvidence({
      entries,
      getValue,
      isSubject,
      lowerIsBetter,
      topCount,
    }),
    placement,
    subject: subjectEntry.subject,
    value: subjectEntry.value,
  };
};

// --- Standings -----------------------------------------------------------

export type StandingsInsightKind = "net-margin" | "defense" | "offense";

export type StandingsTeamLike = {
  stats: { gp?: unknown; oppPts?: unknown; pts?: unknown };
  tid: number;
};

export type StandingsTeamInsight<Team> = {
  conclusion: LeagueRankConclusion<Team>;
  kind: StandingsInsightKind;
};

const getTeamNetMargin = (team: StandingsTeamLike): number | undefined => {
  const pts = toFiniteNumber(team.stats.pts);
  const oppPts = toFiniteNumber(team.stats.oppPts);
  if (pts === undefined || oppPts === undefined) {
    return undefined;
  }
  return pts - oppPts;
};

/**
 * Standings conclusions from the per-game team stats every standings row
 * already carries (pts / oppPts / gp): net margin, points allowed, points
 * scored — each only when the user team ranks top-N league-wide. Candidates
 * are ordered best-rank-first and capped at MAX_STAT_INSIGHTS_PER_PAGE.
 */
export const getStandingsTeamInsights = <Team extends StandingsTeamLike>(
  teams: readonly Team[],
  userTid: number,
  options: {
    maxInsights?: number;
    maxRank?: number;
    minEntries?: number;
    minGames?: number;
  } = {},
): StandingsTeamInsight<Team>[] => {
  const {
    maxInsights = MAX_STAT_INSIGHTS_PER_PAGE,
    maxRank,
    minEntries,
    minGames,
  } = options;

  const userTeam = teams.find((team) => team.tid === userTid);
  const gamesPlayed = userTeam ? toFiniteNumber(userTeam.stats.gp) : undefined;
  const isSubject = (team: Team) => team.tid === userTid;

  const candidates: {
    getValue: (team: Team) => number | undefined;
    kind: StandingsInsightKind;
    lowerIsBetter: boolean;
  }[] = [
    { getValue: getTeamNetMargin, kind: "net-margin", lowerIsBetter: false },
    {
      // A 0.0 points-allowed "leader" is the A3 zero-value poison; per-game
      // points against are only meaningful once they are positive.
      getValue: (team) => {
        const oppPts = toFiniteNumber(team.stats.oppPts);
        return oppPts !== undefined && oppPts > 0 ? oppPts : undefined;
      },
      kind: "defense",
      lowerIsBetter: true,
    },
    {
      getValue: (team) => {
        const pts = toFiniteNumber(team.stats.pts);
        return pts !== undefined && pts > 0 ? pts : undefined;
      },
      kind: "offense",
      lowerIsBetter: false,
    },
  ];

  const insights: StandingsTeamInsight<Team>[] = [];
  for (const candidate of candidates) {
    const conclusion = getLeagueRankConclusion({
      entries: teams,
      gates: {
        gamesPlayed,
        lowerIsBetter: candidate.lowerIsBetter,
        maxRank,
        minEntries,
        minGames,
      },
      getValue: candidate.getValue,
      isSubject,
    });
    if (conclusion) {
      insights.push({ conclusion, kind: candidate.kind });
    }
  }

  return insights
    .sort((a, b) => a.conclusion.placement.rank - b.conclusion.placement.rank)
    .slice(0, Math.max(0, maxInsights));
};

// --- LeagueDashboard ------------------------------------------------------

export type DashboardStatRankLike = {
  rank: unknown;
  stat: string;
  value: unknown;
};

export type DashboardStatInsight = {
  rank: number;
  stat: string;
  value: number;
};

/**
 * Dashboard conclusions from the rank rows the leagueDashboard view already
 * precomputes (pts / oppPts / trb / ast; rank 1 = best, oppPts direction
 * already inverted server-side). Only top-N ranks with real (positive) values
 * qualify; sorted best-rank-first and capped.
 */
export const getDashboardStatRankInsights = (
  teamStats: readonly DashboardStatRankLike[] | undefined,
  options: {
    gamesPlayed: number | undefined;
    maxInsights?: number;
    maxRank?: number;
    minGames?: number;
  },
): DashboardStatInsight[] => {
  const {
    gamesPlayed,
    maxInsights = MAX_STAT_INSIGHTS_PER_PAGE,
    maxRank = DEFAULT_INSIGHT_MAX_RANK,
    minGames = MIN_MEANINGFUL_TEAM_GAMES,
  } = options;

  if (
    !teamStats ||
    gamesPlayed === undefined ||
    !Number.isFinite(gamesPlayed) ||
    gamesPlayed < minGames
  ) {
    return [];
  }

  return teamStats
    .map((entry) => {
      const rank = toFiniteNumber(entry.rank);
      const value = toFiniteNumber(entry.value);
      if (
        rank === undefined ||
        !Number.isInteger(rank) ||
        rank < 1 ||
        rank > maxRank ||
        value === undefined ||
        value <= 0
      ) {
        return undefined;
      }
      return { rank, stat: entry.stat, value };
    })
    .filter((entry): entry is DashboardStatInsight => entry !== undefined)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, Math.max(0, maxInsights));
};

// --- TeamStats -------------------------------------------------------------

export type TeamMetricRankLike = {
  rank: number;
  stat: string;
  value: number;
};

export type TeamStatConclusions<Metric extends TeamMetricRankLike> = {
  strengths: Metric[];
  weaknesses: Metric[];
};

/**
 * TeamStats strength/weakness conclusions: a strength is a genuine top-N
 * league rank, a weakness a genuine bottom-N one — everything in between is
 * not a story. At most MAX_STAT_INSIGHTS_PER_PAGE conclusions total (best
 * strength + worst weakness when both sides qualify). Below the games gate or
 * the league-size floor nothing qualifies and the section must not render.
 */
export const getTeamStatStrengthWeaknessConclusions = <
  Metric extends TeamMetricRankLike,
>({
  gamesPlayed,
  maxConclusions = MAX_STAT_INSIGHTS_PER_PAGE,
  minGames = MIN_MEANINGFUL_TEAM_GAMES,
  minTeams = DEFAULT_INSIGHT_MIN_ENTRIES,
  strengths,
  teamCount,
  topThreshold = DEFAULT_INSIGHT_MAX_RANK,
  weaknesses,
}: {
  gamesPlayed: number | undefined;
  maxConclusions?: number;
  minGames?: number;
  minTeams?: number;
  strengths: readonly Metric[];
  teamCount: number;
  topThreshold?: number;
  weaknesses: readonly Metric[];
}): TeamStatConclusions<Metric> => {
  if (
    gamesPlayed === undefined ||
    !Number.isFinite(gamesPlayed) ||
    gamesPlayed < minGames ||
    teamCount < minTeams
  ) {
    return { strengths: [], weaknesses: [] };
  }

  const qualifiedStrengths = strengths
    .filter(
      (metric) =>
        Number.isInteger(metric.rank) &&
        metric.rank >= 1 &&
        metric.rank <= topThreshold,
    )
    .sort((a, b) => a.rank - b.rank);
  const bottomStart = teamCount - topThreshold + 1;
  const qualifiedWeaknesses = weaknesses
    .filter(
      (metric) =>
        Number.isInteger(metric.rank) &&
        metric.rank >= bottomStart &&
        metric.rank <= teamCount,
    )
    .sort((a, b) => b.rank - a.rank);

  const cap = Math.max(0, maxConclusions);
  if (qualifiedStrengths.length > 0 && qualifiedWeaknesses.length > 0) {
    const perSide = Math.max(1, Math.floor(cap / 2));
    return {
      strengths: qualifiedStrengths.slice(0, perSide),
      weaknesses: qualifiedWeaknesses.slice(0, cap - perSide),
    };
  }

  return {
    strengths: qualifiedStrengths.slice(0, cap),
    weaknesses: qualifiedWeaknesses.slice(0, cap),
  };
};

// --- PlayerStats ------------------------------------------------------------

export type TeamTopScorerInsight<Player> = {
  conclusion: LeagueRankConclusion<Player>;
  player: Player;
};

/**
 * The user team's scoring leader ranked against the whole league. minRank
 * defaults to 2: when that player IS the league leader, the first-screen
 * leader row already tells the story (法则二 — same information unit at most
 * once per page), so no conclusion is emitted.
 */
// The user team's leading scorer (highest positive per-game value among its
// rostered players). Shared by the league-rank-gated top-scorer insight below
// and the ungated playerStats first-screen "team's best" anchor (R6-3), which
// must surface even when that player is nowhere near the league leaders.
export const getTeamTopScorer = <Player>({
  getTid,
  getValue,
  players,
  userTid,
}: {
  getTid: (player: Player) => number | undefined;
  getValue: (player: Player) => number | undefined;
  players: readonly Player[];
  userTid: number;
}): { player: Player; value: number } | undefined => {
  let topScorer: Player | undefined;
  let topValue = 0;
  for (const player of players) {
    if (getTid(player) !== userTid) {
      continue;
    }
    const value = getValue(player);
    if (value !== undefined && value > topValue) {
      topScorer = player;
      topValue = value;
    }
  }

  return topScorer === undefined ? undefined : { player: topScorer, value: topValue };
};

export const getTeamTopScorerInsight = <Player>({
  gamesPlayed,
  getTid,
  getValue,
  maxRank = 10,
  minEntries = DEFAULT_INSIGHT_MIN_ENTRIES,
  minGames,
  minRank = 2,
  players,
  topCount,
  userTid,
}: {
  gamesPlayed: number | undefined;
  getTid: (player: Player) => number | undefined;
  getValue: (player: Player) => number | undefined;
  maxRank?: number;
  minEntries?: number;
  minGames?: number;
  minRank?: number;
  players: readonly Player[];
  topCount?: number;
  userTid: number;
}): TeamTopScorerInsight<Player> | undefined => {
  const top = getTeamTopScorer({ getTid, getValue, players, userTid });

  if (top === undefined) {
    return undefined;
  }
  const subject = top.player;

  const conclusion = getLeagueRankConclusion({
    entries: players,
    gates: {
      gamesPlayed,
      maxRank,
      minEntries,
      minGames,
      minRank,
      topCount,
    },
    getValue,
    isSubject: (player) => player === subject,
  });

  if (!conclusion) {
    return undefined;
  }

  return { conclusion, player: subject };
};
